import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface IRollCategoryStatistics {
  submissions_completed: number;
  submissions_received: number;
  guards_passed: number;
  had_guard_passed: number;
  got_mount: number;
  got_mounted: number;
  got_back_control: number;
  got_back_taken: number;
}

export interface IRollCategoriesStatisticsReturn {
  statistics: IRollCategoryStatistics;
  isLoading: boolean;
  error: Error | null;
}

export const useRollCategoriesStatistics = (userId: string): IRollCategoriesStatisticsReturn => {
  const queryResult = useQuery({
    queryKey: ['roll-categories-statistics', userId],
    queryFn: async (): Promise<IRollCategoryStatistics> => {
      // Fetch all partner_notes for this user with roll_categories
      const { data: partnerNotes, error } = await supabase
        .from('partner_notes')
        .select('roll_categories')
        .eq('user_id', userId)
        .not('roll_categories', 'is', null);

      if (error) {
        throw new Error(`Failed to fetch roll categories: ${error.message}`);
      }

      // Initialize statistics
      const stats: IRollCategoryStatistics = {
        submissions_completed: 0,
        submissions_received: 0,
        guards_passed: 0,
        had_guard_passed: 0,
        got_mount: 0,
        got_mounted: 0,
        got_back_control: 0,
        got_back_taken: 0,
      };

      // Process each partner note's roll_categories
      partnerNotes?.forEach((note) => {
        if (!note.roll_categories || typeof note.roll_categories !== 'object') {
          return;
        }

        const categories = note.roll_categories as Record<string, string>;

        // Submissions
        if (categories.submissions) {
          if (categories.submissions === 'Got Submission') {
            stats.submissions_completed += 1;
          } else if (categories.submissions === 'Got Submitted') {
            stats.submissions_received += 1;
          } else if (categories.submissions === 'Both Submitted') {
            stats.submissions_completed += 1;
            stats.submissions_received += 1;
          }
        }

        // Guard
        if (categories.guard) {
          if (categories.guard === 'Passed Guard') {
            stats.guards_passed += 1;
          } else if (categories.guard === 'Guard Passed') {
            stats.had_guard_passed += 1;
          } else if (categories.guard === 'Both Passed') {
            stats.guards_passed += 1;
            stats.had_guard_passed += 1;
          }
        }

        // Mount
        if (categories.mount) {
          if (categories.mount === 'Gained Mount') {
            stats.got_mount += 1;
          } else if (categories.mount === 'Got Mounted') {
            stats.got_mounted += 1;
          } else if (categories.mount === 'Both Mounted') {
            stats.got_mount += 1;
            stats.got_mounted += 1;
          }
        }

        // Back
        if (categories.back) {
          if (categories.back === 'Gained Back') {
            stats.got_back_control += 1;
          } else if (categories.back === 'Got Back Taken') {
            stats.got_back_taken += 1;
          } else if (categories.back === 'Both Gained Back') {
            stats.got_back_control += 1;
            stats.got_back_taken += 1;
          }
        }
      });

      return stats;
    },
    enabled: !!userId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  return {
    statistics: queryResult.data || {
      submissions_completed: 0,
      submissions_received: 0,
      guards_passed: 0,
      had_guard_passed: 0,
      got_mount: 0,
      got_mounted: 0,
      got_back_control: 0,
      got_back_taken: 0,
    },
    isLoading: queryResult.isLoading,
    error: queryResult.error as Error | null,
  };
};

